/**
 * Determines if the JS Runtime supports Map fully.
 */
export function shouldUseMap(): boolean {
  try {
    const map = new Map([[2, 3]]);
    if (map === map.set(1, 1) && map.get(2) === 3) {
      return true;
    }
  } catch (e) {
    // Map is not defined
  }
  return false;
}

/**
 * `IIterableAsFastMap<T>` provides a normalized means of iterating over a set of key-value pairs with possible early termination.
 */
export interface IIterableAsFastMap<T> {
  /**
   * Iterates over all entries in the collection, evaluating `fn(value, key)` on each entry. Iteration will terminate if the result is
   * truthy, and said result will be returned to the caller.
   * @returns The first truthy value returned by `fn`, or `false` if none were encountered
   * @param fn The operation to perform on each entry. Should return a truthy value if the loop should terminate at the current entry.
   */
  some(fn: (value: T, key: string) => boolean | void): boolean;
}

/**
 * `IFastMap<T>` is an optimized string-keyed dictionary, for which the internal implementation varies based on the target platform.
 * On IE11, for example, it is a plain object. On Chromium, it is a Map. This is because `map.get(key)` is ~10x the performance of
 * `object[key]` on Chromium, at least when the number of keys is large (e.g. >50).
 */
export interface IFastMap<T> extends IIterableAsFastMap<T> {
  /**
   * Gets the value at a particular key, or `undefined` if not present.
   * @param key The key to lookup
   * @returns The value, or `undefined` if not present
   */
  get(key: string): T | undefined;
  /**
   * Determines if a particular key is present in the map.
   * @param key The key to test
   */
  has(key: string): boolean;
  /**
   * Sets a value for a particular key.
   * @returns `this`
   * @param key The key to set
   * @param value The value to set
   */
  set(key: string, value: T): this;
  /**
   * Removes all keys and resets to empty.
   */
  clear(): void;
  /**
   * @returns The set of keys in this map
   */
  keys(): string[];
  /**
   * @returns The number of keys in this map
   */
  size(): number;
}

/**
 * Implementation of `IFastMap<T>` backed by ES6 Map.
 */
class RealMap<T> implements IFastMap<T> {
  public readonly map: Map<string, T>;

  constructor(map?: Map<string, T>) {
    this.map = map || new Map();
  }

  public get(key: string): T | undefined {
    return this.map.get(key);
  }

  public has(key: string): boolean {
    return this.map.has(key);
  }

  public set(key: string, value: T): this {
    this.map.set(key, value);
    return this;
  }

  public clear(): void {
    this.map.clear();
  }

  public keys(): string[] {
    return Array.from(this.map.keys());
  }

  public some(fn: (value: T, key: string) => boolean | void): boolean {
    const iterator = this.map.entries();
    for (let entry = iterator.next(); !entry.done; entry = iterator.next()) {
      const entryValue = entry.value;
      const result = fn(entryValue[1], entryValue[0]);
      if (result) {
        return result;
      }
    }

    return false;
  }

  public size(): number {
    return this.map.size;
  }

  public toString(): string {
    return this.map.toString();
  }
}

/**
 * Partial implementation of `IFastMap<T>` backed by an array of key-value tuples.
 * Used to facilitate normalized iteration.
 */
class ArrayMap<T> implements IIterableAsFastMap<T> {
  public map: [string, T][];

  constructor(data: [string, T][]) {
    this.map = data;
  }

  public some(fn: (value: T, key: string) => boolean | void): boolean {
    for (const entry of this.map) {
      const result = fn(entry[1], entry[0]);
      if (result) {
        return result;
      }
    }
    return false;
  }
}

// For use on IE, since Map is slow there
class FakeMap<T> implements IFastMap<T> {
  public map: { [key: string]: T };
  private _keys: string[] | undefined;

  constructor(obj?: { [key: string]: T }) {
    this.map = obj || Object.create(null);
    this._keys = obj ? undefined : [];
  }

  public get(key: string): T | undefined {
    return this.map[key];
  }

  public has(key: string): boolean {
    return key in this.map;
  }

  public set(key: string, value: T): this {
    this.map[key] = value;
    // Checking to see if this affects the key set is expensive, so just assume it does.
    this._keys = undefined;
    return this;
  }

  public clear(): void {
    this.map = {};
    this._keys = [];
  }

  public keys(): string[] {
    return this._keys || (this._keys = Object.keys(this.map));
  }

  public some(fn: (value: T, key: string) => boolean | void): boolean {
    if (this._keys) {
      for (const key of this.keys()) {
        const result = fn(this.map[key], key);
        if (result) {
          return result;
        }
      }
      return false;
    }

    // Using for-in to optimize early return
    for (const key in this.map) {
      if (Object.prototype.hasOwnProperty.call(this.map, key)) {
        const result = fn(this.map[key], key);
        if (result) {
          return result;
        }
      }
    }

    return false;
  }

  public size(): number {
    return this.keys().length;
  }

  public toString(): string {
    return JSON.stringify(this.map);
  }
}

const useMap = shouldUseMap();

/**
 * The union of types which may be trivially converted to `IIterableAsFastMap<T>` for uniform iteration.
 */
export type IFastMapInput<T> = { [key: string]: T } |
  [string, T][] |
  IIterableAsFastMap<T> |
  Map<string, T>;

/**
 * Determines if a value that is convertible to `IIterableAsFastMap<T>` already is an `IIterableAsFastMap<T>`.
 * @param input The object to test
 */
function isFastMap<T>(
  input: IFastMapInput<T>
): input is IIterableAsFastMap<T> {
  return !!(input && !Array.isArray(input) && (input as { map?: {} }).map && (input as IFastMap<T>).some);
}

/**
 * Converts a key-value dictionary in one of several forms into an `IIterableAsFastMap<T>` to support uniform iteration.
 * @param input The object to enumerate
 */
export function iterableFromData<T>(
  input: IFastMapInput<T>
): IIterableAsFastMap<T> {
  if (Array.isArray(input)) {
    return new ArrayMap(input);
  } else if (input instanceof Map) {
    return new RealMap(input);
  } else if (isFastMap(input)) {
    return input;
  } else {
    return new FakeMap(input);
  }
}

/**
 * Creates an empty `IFastMap<T>`. This is an abstraction layer for a string-indexed Map, which uses ES6 Map when the runtime
 * supports it efficiently, and vanilla Object.create(null) when it does not. The primary value proposition is that ES6 Map is
 * much faster than an Object on Chromium-based browsers, but much slower on IE11.
 * @param useRealMap Whether or not to use ES6 Map as the implementation. Default value selected based on the JS runtime.
 */
export function createFastMap<T>(useRealMap: boolean = useMap): IFastMap<T> {
  return useRealMap ? new RealMap<T>() : new FakeMap<T>();
}